import type {
  AudioItemDetail,
  FetchChaptersFnReturnType,
} from '@listening/api-contract/legacy-client-types';
import { useAtomValue } from '@effect/atom-react';
import { legacyChapterChunkParams } from '@listening/frontend/legacy-reader/chapter-chunks';
import { shallowEqualArrays } from '@listening/utils/ts-utils';
import { AsyncResult } from 'effect/reactivity';
import { useMemo, useState } from 'react';
import {
  legacyChapterChunkKey,
  legacyChapterChunkReader,
  loadedLegacyChapterChunksAtom,
} from './legacy-chapter-chunks';

type ChapterChunkData = FetchChaptersFnReturnType['data'];

export type CachedChapterChunks = ReadonlyArray<ChapterChunkData | undefined>;

/** Keyed by chapter id, not index: a rechunk replaces the chapter list under
 * the same document id, and an index would carry the old chapter's count over
 * to whatever chapter now sits at that position. */
type LearnedParagraphCounts = ReadonlyMap<string, number>;

const NO_LEARNED_COUNTS: LearnedParagraphCounts = new Map();

export const scrollablePrefixSums = (
  paragraphCounts: ReadonlyArray<number>,
) => {
  const result: Array<number> = [];
  let runningTotal = 0;
  for (const [chapterIdx, paragraphCount] of paragraphCounts.entries()) {
    result[chapterIdx] = runningTotal;
    runningTotal += 1 + paragraphCount;
  }
  return result;
};

export const learnChapterParagraphCounts = (
  learned: LearnedParagraphCounts,
  chunk:
    | ReadonlyArray<{ id: string; paragraphs: ReadonlyArray<unknown> }>
    | undefined,
) => {
  if (!chunk) return learned;
  let next: Map<string, number> | undefined;
  for (const chapter of chunk) {
    if (learned.get(chapter.id) === chapter.paragraphs.length) continue;
    next ??= new Map(learned);
    next.set(chapter.id, chapter.paragraphs.length);
  }
  return next ?? learned;
};

export const resolveParagraphCounts = (
  chapters: ReadonlyArray<{ id: string; num_paragraphs: number }>,
  learned: LearnedParagraphCounts,
) =>
  chapters.map((chapter) => learned.get(chapter.id) ?? chapter.num_paragraphs);

const useShallowStableArray = <T>(next: ReadonlyArray<T>) => {
  const [stable, setStable] = useState(next);
  if (shallowEqualArrays(stable, next)) return stable;
  setStable(next);
  return next;
};

// The reader's scrollable index space is built from per-chapter paragraph
// counts. A count learned from a loaded chunk is kept after the chunk is
// dropped, so the index space never shifts back under the viewport.
export function useLegacyChapterCache(documentSummary: AudioItemDetail) {
  const documentId = documentSummary.id;
  const chapters = documentSummary.chapters;
  const chapterIds = useShallowStableArray(
    chapters.map((chapter) => chapter.id),
  );

  const chunkParams = useMemo(
    () =>
      legacyChapterChunkParams(
        documentId,
        chapterIds.map((id) => ({ id })),
      ),
    [documentId, chapterIds],
  );
  // Only chunks already loaded are read, so this never fetches one.
  const loadedKeys = useAtomValue(loadedLegacyChapterChunksAtom);
  const loadedParams = useShallowStableArray(
    chunkParams.filter((params) =>
      loadedKeys.includes(legacyChapterChunkKey(params)),
    ),
  );
  const loadedResults = useAtomValue(
    legacyChapterChunkReader.chunksAtom(loadedParams),
  );
  const cachedChunks: CachedChapterChunks = useMemo(() => {
    const loaded = new Map(
      loadedParams.map((params, i) => [
        legacyChapterChunkKey(params),
        loadedResults[i],
      ]),
    );
    return chunkParams.map((params) => {
      const result = loaded.get(legacyChapterChunkKey(params));
      return result && AsyncResult.getOrElse(result, () => undefined)?.data;
    });
  }, [chunkParams, loadedParams, loadedResults]);

  const [learnedState, setLearnedState] = useState({
    documentId,
    learned: NO_LEARNED_COUNTS,
  });
  const previousLearned =
    learnedState.documentId === documentId
      ? learnedState.learned
      : NO_LEARNED_COUNTS;
  const learned = useMemo(
    () =>
      cachedChunks.reduce(
        (acc, chunk) => learnChapterParagraphCounts(acc, chunk),
        previousLearned,
      ),
    [cachedChunks, previousLearned],
  );
  if (learned !== previousLearned) setLearnedState({ documentId, learned });

  const paragraphCounts = useShallowStableArray(
    resolveParagraphCounts(chapters, learned),
  );

  return { cachedChunks, paragraphCounts };
}
